import Cookies from "js-cookie";

// The single definition of where tokens live on the client. These keys were previously
// repeated as string literals across the provider, the interceptors and the refresh service,
// which is how the access token and the refresh id drifted apart in the first place.
export const ACCESS_TOKEN_KEY = "_internalToken";
export const REFRESH_TOKEN_ID_KEY = "_internalRefreshTokenId";

/**
 * The refresh single-flight key shared by every caller that rotates the browser-wide admin family.
 *
 * One constant, imported rather than spelled out, because two callers that disagree by a character
 * stop sharing a flight and start rotating the same family concurrently — the second presenting a
 * secret the first just rotated out, which trips replay detection and revokes the family.
 *
 * Deliberately a fixed literal and NOT derived from token state: a bootstrap tab with nothing stored
 * would otherwise key differently from a seeded tab holding the same family, which is that same race
 * by another route. Sessions that are genuinely separate — an embed holding an exchanged token — get
 * their own per-instance key instead (AB#10738).
 */
export const ADMIN_SESSION_KEY = "admin";

export interface StoredTokens {
  accessToken: string | null;
  refreshTokenId: string | null;
}

/**
 * Whether a value is shaped like a JWT: a string of three non-empty dot-separated segments.
 *
 * This is the guard the token path was missing. `localStorage.setItem(key, undefined)` does not
 * throw -- it stringifies, so one refresh whose response carried no access token wrote the literal
 * string "undefined" into storage and into the cookie. Every later read then accepted it, because
 * a non-empty string is truthy, and the browser spent the rest of its life either sending
 * `Bearer undefined` or (once the value reached the React provider as a real `undefined`) sending
 * no Authorization header at all. Nothing self-healed: the bad value outlived reloads and
 * re-logins because it was persisted. See AB#10507.
 *
 * It is deliberately a shape check, not a validation: only the server can say whether a token is
 * genuine. The point is to refuse things that were never tokens at all.
 */
export function isJwtShaped(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const parts = value.split(".");
  return parts.length === 3 && parts.every((part) => part.length > 0);
}

/** The first argument that is a usable access token, else null. */
function firstJwt(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (isJwtShaped(value)) return value;
  }
  return null;
}

/**
 * The first argument that is a non-empty string, else null.
 *
 * An empty stored refresh id must read as "nothing stored", not as an empty id. The distinction is
 * load-bearing downstream: TokenRefreshService treats `""` as "this session can never refresh" and
 * refuses to even call the server. That meaning belongs to embed and public-dashboard pages, which
 * hold it in memory -- it must never be something a blank localStorage entry can claim.
 */
function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}

// localStorage throws in private mode and when storage is disabled, so every access is
// guarded. Losing the cache is survivable: cookies still hold the authoritative values.
function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (_) {
    // Coordination degrades to per-tab; the server remains authoritative.
  }
}

function removeLocal(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (_) {
    // Nothing to do — the cookies are cleared separately below.
  }
}

/**
 * Reads both values in one step. They must be read together: pairing an access token from one
 * rotation with a refresh id from another guarantees the next refresh fails.
 */
/**
 * The domain the server writes the token cookies on (`Cookies.SetAnyCookie` uses
 * `DefaultCookieDomain`, i.e. the registrable domain, not the host). js-cookie can only delete a
 * cookie when the domain and path it is given match the ones it was set with, so a bare
 * `Cookies.remove(name)` silently leaves the server-set cookie in place.
 */
function cookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;

  const host = window.location.hostname;
  if (!host || /^[\d.]+$/.test(host) || host === "localhost") return undefined;

  const parts = host.split(".");
  return parts.length > 2 ? "." + parts.slice(-2).join(".") : "." + host;
}

function removeCookie(key: string): void {
  // js-cookie can only delete a cookie when the attributes match the ones it was set with, and
  // this pair is written from two places: the server on the registrable domain, the client
  // host-only. Both spellings are attempted.
  Cookies.remove(key);
  Cookies.remove(key, { path: "/" });

  const domain = cookieDomain();
  if (domain) {
    Cookies.remove(key, { path: "/", domain });
  }
}

/**
 * Deletes a stored access token that is not a token.
 *
 * Ignoring the bad value is not enough on its own. Two admin bundles still read the cookie
 * directly and build an Authorization header from it without any shape check
 * (`Project/ProjectShared.js` and `ProjectImport/project-import-api.js`), so a browser poisoned
 * before this fix shipped would keep sending `Bearer undefined` from those call sites forever.
 * Removing it once, on the first read any React surface does, is what actually heals the browser
 * — which is what AB#10507 asks for: the broken state must not survive a reload.
 */
export function purgeUnusableTokens(): void {
  // Each source is judged on its own. Poisoning is per-store — the two are written by different
  // code on different schedules — so a bad localStorage entry must not take a good cookie with
  // it. Absent is fine and normal; present-but-unusable is the poison.
  const local = readLocal(ACCESS_TOKEN_KEY);
  const localPoisoned = local !== null && !isJwtShaped(local);

  const cookie = Cookies.get(ACCESS_TOKEN_KEY);
  const cookiePoisoned = cookie !== undefined && !isJwtShaped(cookie);

  if (!localPoisoned && !cookiePoisoned) return;

  console.error(
    "Discarding a stored access token that is not a JWT (AB#10507)"
  );

  if (localPoisoned) removeLocal(ACCESS_TOKEN_KEY);
  if (cookiePoisoned) removeCookie(ACCESS_TOKEN_KEY);
}

export function readStoredTokens(): StoredTokens {
  // Heal before reading, so a browser poisoned before this fix shipped stops handing the bad
  // value to the bundles that read the cookie without a guard of their own.
  purgeUnusableTokens();

  // `||`-style fallthrough, not `??`. Nullish coalescing stopped at an empty string, so a blank
  // localStorage entry masked a perfectly good cookie and the session read as unauthenticated.
  // Anything that is not a usable value now falls through to the next source.
  return {
    accessToken: firstJwt(readLocal(ACCESS_TOKEN_KEY), Cookies.get(ACCESS_TOKEN_KEY)),
    refreshTokenId: firstNonEmpty(
      readLocal(REFRESH_TOKEN_ID_KEY),
      Cookies.get(REFRESH_TOKEN_ID_KEY)
    )
  };
}

/**
 * Persists an issued pair. Values that are not usable are dropped rather than written, so a
 * malformed refresh response can never overwrite good credentials with garbage (AB#10507).
 * Returns whether the access token was stored.
 */
export function writeStoredTokens(accessToken: string, refreshTokenId: string): boolean {
  if (!isJwtShaped(accessToken)) return false;

  writeLocal(ACCESS_TOKEN_KEY, accessToken);

  // A missing id leaves the existing one alone: the caller may be rotating an access token
  // against a family whose id it already holds, and blanking it here would strand the session.
  if (typeof refreshTokenId === "string" && refreshTokenId.length > 0) {
    writeLocal(REFRESH_TOKEN_ID_KEY, refreshTokenId);
  }

  return true;
}

/**
 * Best effort, and the gap is load-bearing rather than theoretical.
 *
 * Two things survive this call. `InternalRefreshToken` is **HttpOnly**, so JavaScript cannot touch it
 * at any price — the browser keeps a live refresh secret. And these are bare `Cookies.remove` calls
 * rather than the `removeCookie` helper above, which exists because the server writes on the
 * registrable domain and a bare remove silently leaves that copy in place.
 *
 * So a caller must not read a successful return as "this browser now holds no credentials". The
 * surviving secret with no id beside it is precisely the state that stranded admins in AB#10737, and
 * it is why `/account/refresh` reports that pair as a dead session rather than a malformed request.
 */
export function clearStoredTokens(): void {
  removeLocal(ACCESS_TOKEN_KEY);
  removeLocal(REFRESH_TOKEN_ID_KEY);
  Cookies.remove(ACCESS_TOKEN_KEY);
  Cookies.remove(REFRESH_TOKEN_ID_KEY);
}
